import React from "react";
import {
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Collapse,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  RestartAlt as RestartAltIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
} from "@mui/icons-material";
import {
  ItemSelectionPanel as Panel,
  PanelHeader,
  PanelContent,
  SelectedItemsContainer,
  StyledChip,
  CategoryContainer,
  CategoryHeader,
  ItemList,
} from "./styles";

function ItemSelectionPanel({
  isOpen,
  onTogglePanel,
  selectedItems,
  onToggleItem,
  filteredCategories,
  getFilteredItemsInCategory,
  expandedCategories,
  onToggleCategory,
  onSelectAllInCategory,
  onClearAll,
  searchTerm,
  onSearchTermChange,
  clearSearch,
  getItemName,
  isPinned,
  onTogglePin,
}) {
  return (
    <Panel $isOpen={isOpen} className="item-panel">
      <PanelHeader>
        <Typography variant="subtitle1" fontWeight="bold">
          아이템 선택
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={isPinned ? "고정 해제" : "패널 고정"}>
            <IconButton size="small" onClick={onTogglePin}>
              {isPinned ? (
                <PushPinIcon fontSize="small" color="primary" />
              ) : (
                <PushPinOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClearAll} title="모든 선택 해제">
            <RestartAltIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onTogglePanel} title="닫기">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </PanelHeader>
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          placeholder="아이템 검색..."
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <PanelContent>
        <Typography variant="subtitle2" gutterBottom fontWeight={500}>
          선택된 아이템 ({selectedItems.length})
        </Typography>
        <SelectedItemsContainer>
          {selectedItems.length === 0 ? (
            <Typography color="text.secondary">
              선택된 아이템이 없습니다.
            </Typography>
          ) : (
            selectedItems.map((itemId) => (
              <StyledChip
                key={itemId}
                label={getItemName(itemId)}
                color="primary"
                onDelete={() => onToggleItem(itemId)}
                sx={{ fontWeight: 500 }}
              />
            ))
          )}
        </SelectedItemsContainer>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom fontWeight={500}>
          아이템 목록
        </Typography>
        {filteredCategories.map((category) => {
          const filteredItems = getFilteredItemsInCategory(category);
          const categorySelected = filteredItems.every((itemId) =>
            selectedItems.includes(itemId)
          );
          const someSelected = filteredItems.some((itemId) =>
            selectedItems.includes(itemId)
          );
          if (filteredItems.length === 0) return null;
          return (
            <CategoryContainer key={category}>
              <CategoryHeader>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => onToggleCategory(category)}
                >
                  {expandedCategories[category] ? (
                    <KeyboardArrowDownIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowRightIcon fontSize="small" />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ ml: 1, fontWeight: "medium" }}
                  >
                    {category} ({filteredItems.length})
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={() =>
                    onSelectAllInCategory(category, categorySelected)
                  }
                  sx={{ mr: -1 }}
                >
                  {categorySelected ? (
                    <Checkbox size="small" checked />
                  ) : someSelected ? (
                    <Checkbox size="small" indeterminate />
                  ) : (
                    <Checkbox size="small" />
                  )}
                </IconButton>
              </CategoryHeader>
              <Collapse in={expandedCategories[category]}>
                <ItemList>
                  {filteredItems.map((itemId) => (
                    <StyledChip
                      key={itemId}
                      label={getItemName(itemId)}
                      size="small"
                      variant={
                        selectedItems.includes(itemId) ? "filled" : "outlined"
                      }
                      color={
                        selectedItems.includes(itemId) ? "primary" : "default"
                      }
                      onClick={() => onToggleItem(itemId)}
                      sx={{
                        m: 0.5,
                        fontWeight: selectedItems.includes(itemId) ? 500 : 400,
                      }}
                    />
                  ))}
                </ItemList>
              </Collapse>
            </CategoryContainer>
          );
        })}
        {filteredCategories.length === 0 && (
          <Typography
            color="text.secondary"
            sx={{ py: 2, textAlign: "center" }}
          >
            검색 결과가 없습니다.
          </Typography>
        )}
      </PanelContent>
    </Panel>
  );
}

export default ItemSelectionPanel;
